import React, { useState } from "react";
import { useDispatch } from "react-redux";
import styled from "@emotion/styled";
import { Modal, Col, Row, Button, Form } from "react-bootstrap";
import moment from "moment";
import Loader from "views/universal/Loader/Loader";
import { faInfoCircle, faEdit } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Tippy from "@tippyjs/react";
import { centimesToCurrency } from "utils/utils";
import { ticketRestaurantOperations } from "state/ducks/store/ticketRestaurant";
import { notificationsOperations } from "state/ducks/notifications";
import swal from 'sweetalert';

const PopupAdvantage = (props) => {
  const { show, onHide, onEscapeKeyDown, customerAdvantage, isLoading, customerId } = props;

  const dispatch = useDispatch();
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingAdvantage, setEditingAdvantage] = useState(null);
  const [editAmount, setEditAmount] = useState("");
  const [editNote, setEditNote] = useState("");
  const [isUpdating, setIsUpdating] = useState(false);

  require("moment/locale/fr.js");

  const handleEditClick = (advantage) => {
    setEditingAdvantage(advantage);
    setEditAmount((advantage.amount / 100).toString());
    setEditNote(advantage.note || "");
    setShowEditModal(true);
  };

  const handleCloseEditModal = () => {
    setShowEditModal(false);
    setEditingAdvantage(null);
    setEditAmount("");
    setEditNote("");
  };

  const handleUpdateAmount = (e) => {
    e.preventDefault();
    const amountValue = parseFloat(editAmount);
    if (!editingAdvantage || editAmount === "" || isNaN(amountValue) || amountValue < 0) {
      return swal({
        title: "Erreur",
        text: "Veuillez saisir un montant valide (0 ou supérieur)",
        icon: "error",
      });
    }

    const newAmount = parseFloat(editAmount);
    swal({
      title: "Modifier le montant",
      text: `Êtes-vous sûr de vouloir modifier le montant de ${(editingAdvantage.amount / 100).toFixed(2)}€ à ${newAmount.toFixed(2)}€ ?`,
      icon: "warning",
      buttons: {
        cancel: "Annuler",
        confirm: "Confirmer",
      },
    }).then((confirm) => {
      if (confirm) {
        setIsUpdating(true);
        if (!customerId) {
          setIsUpdating(false);
          return dispatch(notificationsOperations.getResponseError('ID client manquant.'));
        }
        const amountInCents = Math.round(newAmount * 100);
        
        dispatch(ticketRestaurantOperations.updateAdvantageAmount(
          customerId,
          editingAdvantage.id,
          amountInCents,
          editNote
        )).then(
          (result) => {
            setIsUpdating(false);
            handleCloseEditModal();
            dispatch(notificationsOperations.getResponseSuccess('Montant et note modifiés avec succès.'));
            // Rafraîchir les avantages
            dispatch(ticketRestaurantOperations.getCustomerAdvantage(customerId));
          },
          (error) => {
            setIsUpdating(false);
            dispatch(notificationsOperations.getResponseError('Erreur lors de la modification du montant.'));
          }
        );
      }
    });
  };

  return (
    <>
    <Popup
      size="lg"
      show={show}
      onHide={onHide}
      onEscapeKeyDown={onEscapeKeyDown}
    >
      {isLoading ? (
        <Loader items={2} />
      ) : (
        <>
          {customerAdvantage ? (
            <>
              <Row>
                <Col>
                  <Title className="text-center">
                    <small>
                      Avantages de{" "}
                      {customerAdvantage.firstname &&
                        customerAdvantage.firstname}{" "}
                      {customerAdvantage &&
                        customerAdvantage.lastname &&
                        customerAdvantage.lastname}
                    </small>
                    <br />
                    <span>
                      {centimesToCurrency(
                        customerAdvantage.availableAdvantageAmount
                      )}
                    </span>
                  </Title>
                </Col>
              </Row>
              <Header noGutters>
                <Col sm={2}>
                  <p>
                    <b>Type</b>
                  </p>
                </Col>
                <Col sm={2}>
                  <p>
                    <b>Date de livraison</b>
                  </p>
                </Col>
                <Col sm={2}>
                  <p>
                    <b>Date d'expiration/parrainé</b>
                  </p>
                </Col>
                <Col sm={2}>
                  <p>
                    <b>Montant</b>
                  </p>
                </Col>
                <Col sm={2}>
                  <p>
                    <b>Produit offert</b>
                  </p>
                </Col>
                <Col sm={1}>
                  <Tippy
                    placement="left"
                    content={
                      <span>
                        Fiche prod de la commande qui a généré le cagnottage
                      </span>
                    }
                  >
                    <p>
                      <b>Prod</b>{" "}
                      <FontAwesomeIcon icon={faInfoCircle} size="sm" />
                    </p>
                  </Tippy>
                </Col>
                <Col sm={1}>
                  <p>
                    <b>Actions</b>
                  </p>
                </Col>
              </Header>

              {customerAdvantage.advantages.map((a, index) => (
                <Operation key={a.id || index} type={a.type} noGutters>
                  <Col sm={2}>
                    <p>
                      {a.advantageName === "percentOrder" ? 
                        <>Gourmand</>
                       : a.advantageName === "sponsorship" ? 
                        <>Parrainage</>
                       : a.advantageName === "percentEarlyOrder" ? 
                       <>Matinal</>
                       : a.advantageName === "firstOrder" ? 
                       <>Première commande</>
                       : a.advantageName === "sav" ? 
                       <>SAV</>
                       : a.advantageName === "companySubscription" ? 
                       <>Cantine</>
                       : a.advantageName === "companionPercentOrder" ? 
                       <>Compagnon</>
                       : null}
                    </p>
                  </Col>
                  <Col sm={2}>
                    <p>
                      <b>
                        {a.date &&
                          moment(a.date).format("DD/MM/YY")}
                      </b>
                    </p>
                  </Col>
                  <Col sm={2}>
                    <p>
                      <b>
                        {a.expirationDate &&
                          moment(a.expirationDate).format("DD/MM/YY")}
                        {a.sponsoredName && a.sponsoredName}
                      </b>
                    </p>
                  </Col>
                  <Col sm={2}>
                    <p>
                      <b>
                        {a.amount &&
                        a.type === "debit" &&
                        a.advantageName !== "sponsorship"
                          ? centimesToCurrency(-1 * a.amount)
                          : a.amount &&
                            a.type === "credit" &&
                            a.advantageName !== "sponsorship"
                          ? '+'+centimesToCurrency(a.amount)
                          : null}
                      </b>
                    </p>
                  </Col>
                  <Col sm={2}>
                    <p>
                      {a.compensationLabel ? <b>{a.compensationLabel}</b> : '—'}
                    </p>
                  </Col>
                  <Col sm={1}>
                    {" "}
                    {a.prodPageLink &&
                      <p>
                        <Link href={a.prodPageLink} target="_blank">
                          Fiche
                        </Link>
                      </p>
                    }
                    {a.freshdeskTicketLink &&
                      <p>
                        <Link href={a.freshdeskTicketLink} target="_blank" rel="noopener noreferrer">
                          Ticket
                        </Link>
                      </p>
                    }
                  </Col>
                  <Col sm={1}>
                    {a.advantageName === 'sav' && a.type === 'credit' && a.id && (
                      <Tippy content={<span>Éditer le montant</span>}>
                        <EditButton onClick={() => handleEditClick(a)}>
                          <FontAwesomeIcon icon={faEdit} size="sm"/>
                        </EditButton>
                      </Tippy>
                    )}
                  </Col>
                </Operation>
              ))}
            </>
          ) : (
            "Pas d'avantage pour ce client."
          )}
        </>
      )}
    </Popup>

    <EditModal
      show={showEditModal}
      onHide={handleCloseEditModal}
      editingAdvantage={editingAdvantage}
      editAmount={editAmount}
      setEditAmount={setEditAmount}
      editNote={editNote}
      setEditNote={setEditNote}
      handleUpdateAmount={handleUpdateAmount}
      isUpdating={isUpdating}
    />
    </>
  );
};

const Popup = styled(Modal)`
  .modal-content {
    background-color: #ecf0f5 !important;
  }
  p {
    margin-bottom: 0rem;
  }
`;

const Title = styled.h3`
  span {
    font-weight: bold;
    color: ${(props) => props.theme.colors.green};
  }
`;

const Header = styled(Row)`
  color: grey;
  padding: 0.6rem;
  font-size: 0.8rem;
`;

const Link = styled.a`
  color: ${(props) => props.theme.colors.green};
  text-decoration: underline;
  text-align: right;
`;

const Operation = styled(Row)`
  padding: 0.6rem;
  background-color: white;
  margin-bottom: 0.5rem;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.2);
  p{
    color: ${(props) => props.type === "credit" ? props.theme.colors.green : props.type === "debit" ? props.theme.colors.grey : props.theme.colors.black};
  }
`;

const EditButton = styled.button`
  background: none;
  border: none;
  color: ${(props) => props.theme.colors.blue || '#007bff'};
  cursor: pointer;
  padding: 0.25rem;
  &:hover {
    color: ${(props) => props.theme.colors.darkblue || '#0056b3'};
  }
`;

const EditModal = ({ show, onHide, editingAdvantage, editAmount, setEditAmount, editNote, setEditNote, handleUpdateAmount, isUpdating }) => {
  return (
    <Modal show={show} onHide={onHide}>
      <Modal.Header closeButton>
        <Modal.Title>Éditer le montant</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {editingAdvantage && (
          <Form onSubmit={handleUpdateAmount}>
            <Form.Group>
              <Form.Label>Montant actuel</Form.Label>
              <Form.Control
                type="text"
                value={(editingAdvantage.amount / 100).toFixed(2) + ' €'}
                disabled
              />
            </Form.Group>
            <Form.Group>
              <Form.Label>Nouveau montant (€)</Form.Label>
              <Form.Control
                type="number"
                step="0.01"
                min="0"
                value={editAmount}
                onChange={(e) => setEditAmount(e.target.value)}
                required
                autoFocus
              />
            </Form.Group>
            <Form.Group>
              <Form.Label>Note</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                value={editNote}
                onChange={(e) => setEditNote(e.target.value)}
                placeholder="Note sur l'avantage"
              />
            </Form.Group>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.5rem', marginTop: '1rem' }}>
              <Button variant="secondary" onClick={onHide} disabled={isUpdating}>
                Annuler
              </Button>
              <Button variant="primary" type="submit" disabled={isUpdating}>
                {isUpdating ? 'Mise à jour...' : 'Confirmer'}
              </Button>
            </div>
          </Form>
        )}
      </Modal.Body>
    </Modal>
  );
};

PopupAdvantage.propTypes = {};

export default PopupAdvantage;
